import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

// ============================================================
// The two outbound contact channels the app already uses, in one place.
//
//   WhatsApp — the number wired into the app header's chat button
//              (components/Appheader).
//   Calendly — the booking link the corporate-services screen books
//              consultations through (components/CorporateServicesView).
//
// Collected here so the pre-login services showcase reaches a prospect
// through the same channels a signed-in owner does, rather than pasting
// a second copy of the number and the link.
// ============================================================

export const WHATSAPP_NUMBER = '971526177472';

/**
 * Property management is answered on its own line (+971 52 886 2187) by the
 * team that actually runs the buildings, so those enquiries must not land in
 * the general sales inbox. Passed explicitly at the call site rather than
 * switched on inside this module - a number that changes silently depending
 * on which page you came from is the kind of thing nobody finds for months.
 */
export const WHATSAPP_PM_NUMBER = '971528862187';

/**
 * Tenants are answered on their own line (+971 50 951 1074) — a tenant with a
 * leaking tap must not land in the sales inbox, and the team that handles
 * leases is not the team that handles buyers. Passed explicitly at the call
 * site, like the PM number above.
 */
export const WHATSAPP_TENANT_NUMBER = '971509511074';

/**
 * Golden Visa enquiries go to +971 50 951 1074, the same phone as the tenant
 * line. Named separately so moving visas to another number later is a
 * one-line change that cannot touch the tenant line.
 */
export const WHATSAPP_VISA_NUMBER = '971509511074';

export const CALENDLY_URL =
  'https://calendly.com/j-kijasko-investmenter/internationale-steuerberatung-investmenter-app';

/** wa.me deep link, message pre-filled. Defaults to the general number. */
export function whatsappUrl(message: string, number: string = WHATSAPP_NUMBER): string {
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

/**
 * Opens WhatsApp. Uses window.open on every platform — the same call the
 * app header makes — so native builds hand the wa.me link to the installed
 * WhatsApp app instead of rendering it inside an in-app webview.
 */
export function openWhatsApp(message: string, number: string = WHATSAPP_NUMBER): void {
  window.open(whatsappUrl(message, number), '_blank');
}

/**
 * Opens the booking page inside the app: Calendly's own popup widget (loaded
 * in public/index.html; calendly.com is in the hosting CSP frame-src), with
 * the owner's name and e-mail filled in when known. Only if the widget script
 * failed to load does it fall back to the in-app browser on native, or a new
 * tab on the web — matching how the policy links behave (see shared/legal).
 */
export async function openCalendly(prefill?: { name?: string; email?: string }): Promise<void> {
  const widget = (window as any).Calendly;
  if (widget && typeof widget.initPopupWidget === 'function') {
    widget.initPopupWidget({
      url: CALENDLY_URL,
      prefill: { name: prefill?.name || '', email: prefill?.email || '' },
    });
    return;
  }
  if (Capacitor.isNativePlatform()) {
    try {
      await Browser.open({ url: CALENDLY_URL });
      return;
    } catch {
      /* fall through to a normal window.open */
    }
  }
  window.open(CALENDLY_URL, '_blank');
}
